import { getTimeStampFromDateString } from "components/kelas-live/utils";
import { format } from "date-fns";
import { useGetCoursesById } from "hooks/use-get-courses-by-ids/use-get-courses-by-ids";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ISection, ISlot } from "services/api/packages/packageDetails.types";
import { DAY_WEIGHT } from "constants/constants";
import trackEvent from "services/mixpanel/mixpanel";
import { slotSelectionEventNames } from "services/mixpanel/slot-selection-events";
import { useAuth } from "stores/auth/auth-context";
import {
  getDayMap,
  getIndonesianTimezone,
  getRelativeDateToTodayDateOnly
} from "utils/utils";
import {
  IModal,
  ISectionDatWithFlag,
  ISlotSelectionClosed,
  ISlotSelectionClosedPage,
  IUseSlotSelection
} from "./slot-selection.types";
import { useKelasLiveContext } from "stores/kelas-live/kelas-live-context";
import React from "react";

interface Props {
  isEligibleToSkip: boolean;
}

const useSlotSelection: (args: Props) => IUseSlotSelection = ({
  isEligibleToSkip
}) => {
  const router = useRouter();
  const { pathname, query } = router;
  const { currentStep } = query || {};
  const { user } = useAuth();
  const { sectionsData, isLoading } = useGetCoursesById({
    router,
    onSuccess: handleSectionDataWithFlag
  });
  const [step, setStep] = useState(1);
  const [sectionsDatWithFlag, setSectionsDataWithFlag] = useState<
    ISectionDatWithFlag[]
  >([]);
  const [modal, setModal] = useState("");

  const progressRef = useRef<number>(1);

  const [selectedSection, setSelectedSection] = React.useState<number>(1);

  const {
    user: {
      userData: { primary_user }
    }
  } = useKelasLiveContext();

  useEffect(() => {
    router.beforePopState(({ as }) => {
      if (as !== router.asPath) {
        slotSelectionClosedEvent(
          ISlotSelectionClosed.HARDWARE_BACK_BUTTON,
          currentStep || "1" >= "2"
            ? ISlotSelectionClosedPage.INTERMEDIATE_PAGE
            : ISlotSelectionClosedPage.FIRST_PAGE
        );
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router]);

  useEffect(() => {
    if (sectionsData.length > 0) {
      const currentSection = sectionsData?.[step - 1];
      const mixpanelPayload = {
        totalCourseToSelectCount: sectionsData.length,
        currentPageCourseName: currentSection.title,
        slotOptionForCurrentCourse: currentSection.slots.map(
          (slot) => slot.name
        ),
        withTeacherName: !!currentSection.slots.find(
          (slot) => slot.teacher?.name
        ),
        skippable: isEligibleToSkip
      };

      trackEvent({
        eventName: slotSelectionEventNames.pageViewed,
        payload: mixpanelPayload
      });

      if (currentSection.slots.length === 0) {
        const mixpanelPayload = {
          coursePosition: step,
          courseName: currentSection.title,
          courseId: currentSection.course_id,
          withTeacherName: !!currentSection.slots.find(
            (slot) => slot.teacher?.name
          )
        };
        trackEvent({
          eventName: slotSelectionEventNames.noSlotAvailable,
          payload: mixpanelPayload
        });
      }
    }
  }, [sectionsData, step]);

  function handleSectionDataWithFlag(sectionsData: ISection[]) {
    setSectionsDataWithFlag(
      sectionsData.map((section) => {
        const { slots } = section || {};
        return {
          ...section,
          selected_slot:
            section.is_optional && slots.length === 1
              ? {
                  is_optional: section.is_optional,
                  slot: section.slots[0],
                  formatted_schedule: ""
                }
              : undefined
        };
      })
    );
  }

  useEffect(() => {
    if (sectionsData.length > 0) {
      handleSectionDataWithFlag(sectionsData);
    }
  }, [sectionsData]);

  const filteredSections = useMemo(() => {
    return sectionsDatWithFlag.filter(
      (section) =>
        (section.is_optional && section.slots.length > 1) ||
        !section.is_optional
    );
  }, [sectionsDatWithFlag]);

  useEffect(() => {
    if (filteredSections.length === 0 && !isLoading) {
      setModal(IModal.CONFIRMATION);
    }
  }, [filteredSections, isLoading]);

  const handleNextSection = () => {
    const mixpanelPayload = {
      buttonPostion: step,
      currentPageCourseName: sectionsDatWithFlag[step - 1]?.title,
      slotOptionForCurrentCourse: sectionsDatWithFlag[step - 1]?.slots.map(
        (slot) => slot.name
      ),
      selectedSlotForCurrentCourse:
        sectionsDatWithFlag[step - 1].selected_slot?.slot.name
    };
    trackEvent({
      eventName: slotSelectionEventNames.nextButton,
      payload: mixpanelPayload
    });
    if (step === filteredSections.length) {
      setModal(IModal.CONFIRMATION);
      return;
    }
    const updatedQuery = {
      ...query,
      currentStep: String(step + 1)
    };
    router.push({
      pathname,
      search: new URLSearchParams(updatedQuery).toString()
    });
    setStep((prevState) => prevState + 1);
    setSelectedSection(step + 1);
    if (step + 1 > progressRef.current)
      progressRef.current = progressRef.current + 1;
  };

  const handleClickSection = (currentStep: number) => {
    setStep(currentStep);
  };

  const handleSelectSlot = (courseId: string, slot: ISlot) => {
    let slotName = "";
    let withTeacherName = false;
    const updatedData = sectionsDatWithFlag.map((section) => {
      if (section.course_id === courseId) {
        if (section.selected_slot?.slot.id === slot.id) {
          return {
            ...section,
            selected_slot: undefined
          };
        }
        slotName = slot.name;
        withTeacherName = !!slot.teacher?.name;
        return {
          ...section,
          selected_slot: {
            is_optional: section.is_optional,
            slot,
            formatted_schedule: `${slot.days
              .map((days) => getDayMap[days])
              .join(", ")} • ${formatTime(slot.start_time)} - ${formatTime(
              slot.end_time
            )} ${getIndonesianTimezone()} `
          }
        };
      } else {
        return { ...section };
      }
    });

    if (slotName) {
      const mixpanelPayload = {
        coursePosition: step,
        slotName,
        withTeacherName
      };
      trackEvent({
        eventName: slotSelectionEventNames.coursePicked,
        payload: mixpanelPayload
      });
    }
    setSectionsDataWithFlag(updatedData);
  };

  const getOrderedSlots = (slotItems: ISlot[]): ISlot[] => {
    const todayDate = new Date();
    const tomorrowDate = getRelativeDateToTodayDateOnly(1);
    return slotItems
      .filter((slot) => slot.remaining_seats > 0)
      .map((slot) => {
        const slotStartDate = new Date(slot.start_date);
        // removing time from the startDate
        const slotStartDateWithoutTime = new Date(
          slotStartDate.getFullYear(),
          slotStartDate.getMonth(),
          slotStartDate.getDate()
        );
        return {
          ...slot,
          orderStartDate:
            tomorrowDate.getTime() > slotStartDateWithoutTime.getTime()
              ? todayDate.getTime()
              : slotStartDateWithoutTime.getTime(),
          orderDayWeight: DAY_WEIGHT[slot.days[0]],
          orderStartTime:
            new Date(slot.start_time).getHours() * 60 +
            new Date(slot.start_time).getMinutes()
        };
      })
      .sort((a, b) => {
        const aDate = a.orderStartDate;
        const bDate = b.orderStartDate;
        if (aDate == bDate) {
          const aDayWeight = a.orderDayWeight;
          const bDayWeight = b.orderDayWeight;
          if (aDayWeight == bDayWeight) {
            const aTime = a.orderStartTime;
            const bTime = b.orderStartTime;
            return aTime - bTime;
          }
          return aDayWeight - bDayWeight;
        }
        return aDate - bDate;
      });
  };

  const getClashesSlot = (slot: ISlot, section: ISectionDatWithFlag) => {
    if (section.is_optional) return undefined;

    let clashesSubject;
    const seletedSlots = sectionsDatWithFlag.map(
      (section) => section.selected_slot
    );

    seletedSlots.forEach((selectedSlot) => {
      if (selectedSlot && !selectedSlot.is_optional) {
        const courseChecking = slot.course.id !== selectedSlot?.slot.course.id;
        const daysChecking = slot.days.some((item) =>
          selectedSlot?.slot.days.includes(item)
        );
        const timeChecking =
          formatTime(slot.start_time) <=
            formatTime(selectedSlot.slot.end_time) &&
          formatTime(slot.end_time) >= formatTime(selectedSlot.slot.start_time);
        if (courseChecking && daysChecking) {
          if (timeChecking) {
            clashesSubject = sectionsDatWithFlag.find(
              (sec) => sec.selected_slot === selectedSlot
            )?.subject;
            return;
          }
        }
      }
    });
    return clashesSubject;
  };

  const formatTime = (value: string) =>
    format(getTimeStampFromDateString(value), "kk:mm");

  const handleCloseModal = () => {
    setModal("");
    slotSelectionClosedEvent(
      ISlotSelectionClosed.CROSS_BUTTON,
      ISlotSelectionClosedPage.SLOT_CONFIRMATION_PAGE
    );
  };

  const handleFinishModalConfirmation = () => {
    setModal(IModal.SUCCESS);
  };

  const isDisabledNextButton = () => {
    return (
      filteredSections.length === 0 ||
      filteredSections
        .slice(0, step)
        .filter((section) => !section.selected_slot).length > 0
    );
  };

  const slotSelectionClosedEvent = (
    source: ISlotSelectionClosed,
    currentPage: ISlotSelectionClosedPage
  ) => {
    const mixpanelPayload = {
      source,
      currentPage
    };
    trackEvent({
      eventName: slotSelectionEventNames.slotClosed,
      payload: mixpanelPayload
    });
  };

  const handleClickHelp = () => {
    setModal(IModal.CALL_ME_BACK_CONFIRMATION);
    trackEvent({
      eventName: slotSelectionEventNames.contactKakakSiaga,
      payload: { source: "Slot_Selection" }
    });
  };

  const handleSlotSelectionSkip = () => {
    trackEvent({
      eventName: slotSelectionEventNames.slotSelectionSkipped,
      payload: {}
    });
  };

  return {
    fullName: user.userData.primary_user?.full_name || "",
    stepLength: filteredSections.length,
    progressRef: progressRef.current,
    step,
    sectionsData: sectionsDatWithFlag,
    isLoading,
    selectedSection,
    setSelectedSection,
    handleNextSection,
    handleClickSection,
    handleSelectSlot,
    getClashesSlot,
    formatTime,
    modal,
    handleCloseModal,
    handleFinishModalConfirmation,
    isDisabledNextButton,
    phoneNumber: primary_user.phone_number,
    primaryUser: primary_user,
    handleClickHelp,
    setModal,
    getOrderedSlots,
    filteredSections,
    handleSlotSelectionSkip
  };
};

export { useSlotSelection };
